/**
 * E2E team-key distribution for organization BYOS remotes (Bodega
 * P-1), built on the `e2e-crypto` kit (canon Boveda/P-3, ZERO_KNOWLEDGE_VAULT
 * §3.4 "organization vault — key distribution per member").
 *
 * Model:
 *   1. Each user creates ONE E2E identity: a P-256 ECDH pair whose private
 *      side is wrapped with a key derived from their passphrase (Argon2id → HKDF →
 *      AES-GCM). The server stores ONLY opaque blobs (public + wrapped
 *      private + KDF salt/params): multi-device without losing
 *      zero-knowledge.
 *   2. A remote's team key (32 random bytes, NEVER derived from
 *      any password) is sealed to each member's public key (ECIES)
 *      and the server stores the envelopes. No one without a member's private
 *      key can open any of them — including the server.
 *   3. Revocation = rotation: epoch+1, new key, distribution only to those
 *      who remain (the AAD binds each envelope to `remoteId:epoch`, so an old
 *      envelope cannot be reused as if it were from the new epoch).
 *
 * Everything in this module runs IN THE CLIENT. The invariant is tested by
 * the tests in `__tests__/team-keys.test.ts` (round-trip, non-member cannot open,
 * incorrect passphrase fails, wrong epoch/remote fail).
 /** Waits for the app to finish booting (skeleton overlay disappears). */

import { base64ToBytes, bytesToBase64, bytesToHex } from "./encoding";
import {
  generateKeyPair,
  openWithPrivateKey,
  type SealedToPublicKey,
  sealToPublicKey,
} from "./asymmetric";
import { type Argon2Params, deriveKdfKey, KDF_INTERACTIVE, newKdfSalt } from "./kdf";
import { aeadOpen, aeadSeal, hkdfExpand, importAesKey, randomBytes, sha256 } from "./primitives";

export type { Argon2Params, SealedToPublicKey };

const TEAM_KEY_BYTES = 32;

/** Identity blobs as stored on the server (opaque). */
export interface E2eIdentityBlobs {
  /** ECDH P-256 public key (raw, base64). Public. */
  readonly publicKey: string;
  /** PKCS#8 private key wrapped with the passphrase (AES-GCM, base64). */
  readonly protectedPrivateKey: string;
  /** Argon2id salt (base64). Public. */
  readonly kdfSalt: string;
  /** Argon2id parameters used. */
  readonly kdfParams: Argon2Params;
}

function identityAad(publicKey: string): string {
  return `bodega:e2e-identity:v1:${publicKey}`;
}

function teamKeyAad(remoteId: string, keyEpoch: number): string {
  return `bodega:team-key:v1:${remoteId}:${keyEpoch}`;
}

async function identityWrapKey(
  passphrase: string,
  salt: Uint8Array,
  params: Argon2Params,
): Promise<CryptoKey> {
  const master = deriveKdfKey(passphrase, salt, params);
  const wrapRaw = await hkdfExpand(master, "bodega:e2e-crypto:v1:identity-wrap");
  return importAesKey(wrapRaw);
}

/**
 * Creates the user's E2E identity: generates the ECDH pair and wraps the private
 * key with the passphrase. The result is uploaded as-is (opaque storage).
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export async function createE2eIdentity(
  passphrase: string,
  params: Argon2Params = KDF_INTERACTIVE,
): Promise<E2eIdentityBlobs> {
  const pair = await generateKeyPair();
  const salt = newKdfSalt();
  const wrapKey = await identityWrapKey(passphrase, salt, params);
  const sealed = await aeadSeal(
    wrapKey,
    base64ToBytes(pair.privateKey),
    identityAad(pair.publicKey),
  );
  return {
    publicKey: pair.publicKey,
    protectedPrivateKey: bytesToBase64(sealed),
    kdfSalt: bytesToBase64(salt),
    kdfParams: params,
  };
}

/**
 * Unlocks the identity with the passphrase → private key (base64 PKCS#8,
 * ONLY in client memory). An incorrect passphrase ⇒ AEAD open throws.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export async function unlockE2eIdentity(
  passphrase: string,
  identity: E2eIdentityBlobs,
): Promise<string> {
  const wrapKey = await identityWrapKey(
    passphrase,
    base64ToBytes(identity.kdfSalt),
    identity.kdfParams,
  );
  const priv = await aeadOpen(
    wrapKey,
    base64ToBytes(identity.protectedPrivateKey),
    identityAad(identity.publicKey),
  );
  return bytesToBase64(priv);
}

/** New team key (random — never derived from a password). */
export function createTeamKey(): Uint8Array {
  return randomBytes(TEAM_KEY_BYTES);
}

export interface TeamMemberPublicKey {
  readonly userId: string;
  readonly publicKey: string;
}

export interface SealedTeamShare {
  readonly userId: string;
  /** Serialized ECIES envelope (JSON `{epk, env}`) — opaque to the server. */
  readonly sealedKey: string;
}

/** Seals the team key to each member (create, add member or rotate). */
export async function distributeTeamKey(
  remoteId: string,
  keyEpoch: number,
  teamKey: Uint8Array,
  members: readonly TeamMemberPublicKey[],
): Promise<SealedTeamShare[]> {
  const aad = teamKeyAad(remoteId, keyEpoch);
  const out: SealedTeamShare[] = [];
  for (const member of members) {
    const sealed = await sealToPublicKey(member.publicKey, teamKey, aad);
    out.push({ userId: member.userId, sealedKey: JSON.stringify(sealed) });
  }
  return out;
}

/** A member opens their sealed share with their unlocked private key. */
export async function openTeamKeyShare(
  privateKeyB64: string,
  share: { remoteId: string; keyEpoch: number; sealedKey: string },
): Promise<Uint8Array> {
  // Trust boundary: the envelope was written by this app's distribution flow; if
  // the blob were corrupt, AEAD open would fail anyway.
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  const sealed = JSON.parse(share.sealedKey) as SealedToPublicKey;
  return openWithPrivateKey(privateKeyB64, sealed, teamKeyAad(share.remoteId, share.keyEpoch));
}

/**
 * Short, stable fingerprint of the team key (8 hex of its SHA-256): allows
 * two members to visually confirm that they open THE SAME key without showing it.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export async function teamKeyFingerprint(teamKey: Uint8Array): Promise<string> {
  return bytesToHex(await sha256(teamKey)).slice(0, 8);
}
